import { useCallback, useId, useState } from 'react';

import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { logOut } from 'mastodon/utils/log_out';

import type { BaseConfirmationModalProps } from './confirmation_modal';
import { ConfirmationModal } from './confirmation_modal';

const messages = defineMessages({
  logoutTitle: { id: 'confirmations.logout.title', defaultMessage: 'Log out?' },
  logoutMessage: {
    id: 'confirmations.logout.message',
    defaultMessage: 'Are you sure you want to log out?',
  },
  logoutConfirm: {
    id: 'confirmations.logout.confirm',
    defaultMessage: 'Log out',
  },
});

export const ConfirmLogOutModal: React.FC<BaseConfirmationModalProps> = ({
  onClose,
}) => {
  const intl = useIntl();
  // Unticked by default: a plain logout must leave this browser's encryption
  // keys intact, otherwise signing back in looks like a brand-new device and
  // every earlier message becomes undecryptable here (kanban #15).
  const [wipeE2ee, setWipeE2ee] = useState(false);
  const checkboxId = useId();

  const handleToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWipeE2ee(e.target.checked);
    },
    [setWipeE2ee],
  );

  const onConfirm = useCallback(() => {
    void logOut({ wipeE2ee });
  }, [wipeE2ee]);

  return (
    <ConfirmationModal
      title={intl.formatMessage(messages.logoutTitle)}
      message={intl.formatMessage(messages.logoutMessage)}
      confirm={intl.formatMessage(messages.logoutConfirm)}
      onConfirm={onConfirm}
      onClose={onClose}
      extraContent={
        <div className='confirm-logout__e2ee'>
          <label className='confirm-logout__e2ee-label' htmlFor={checkboxId}>
            <input
              id={checkboxId}
              type='checkbox'
              checked={wipeE2ee}
              onChange={handleToggle}
            />
            <span>
              <FormattedMessage
                id='confirmations.logout.wipe_e2ee'
                defaultMessage='Also erase encrypted messages on this device'
              />
            </span>
          </label>
          <p className='confirm-logout__e2ee-hint'>
            {wipeE2ee ? (
              <FormattedMessage
                id='confirmations.logout.wipe_e2ee_warning'
                defaultMessage="This device will forget its encryption keys. Messages you have already received here can't be read again on this browser, and they can't be recovered — not even by us."
              />
            ) : (
              <FormattedMessage
                id='confirmations.logout.wipe_e2ee_hint'
                defaultMessage='Leave this unchecked to keep your encrypted conversations readable when you log back in. Check it on a shared or public computer.'
              />
            )}
          </p>
        </div>
      }
    />
  );
};
